/**
 * LiveLanding — premium black + gold marketing page for lamirage.live
 *
 * Rendered at lamirage.live / www.lamirage.live via host-based routing in App.tsx.
 * Account-less, public marketing page — no auth, no required data fetching.
 *
 * Theme: near-black + champagne gold. Shared kit from @/lib/liveTheme +
 *        @/components/live/LiveUI for full visual consistency with LiveWatch.
 *        Reuses the watch page's cinematic vocabulary (.lw-player-frame,
 *        .lw-player-glow, .lw-rise, .lw-player-fade, .lw-expect) so the
 *        landing and watch pages read as one family.
 *
 * Hero video is a CONTENT DEPENDENCY that may not exist yet — the hero frame
 * is built fallback-first: it looks intentional and premium with NO video file
 * present (themed aurora bg + always-on overlay chrome). The <video> at
 * /live/hero-loop.mp4 (+ .webm, + poster) degrades gracefully (onError hides
 * it; reduced-motion shows poster only).
 *
 * Scroll animations: framer-motion whileInView (once:true), staggered children.
 * Respects prefers-reduced-motion — framer's useReducedMotion gates all motion.
 *
 * Wow pass (2026-07-03): hero mouse+scroll parallax (3 planes, spring), player
 * 3D tilt, gold dust, scroll-cue; 3D gear showcase; cinematic full-bleed band;
 * animated stat counters; city marquee; hairline-draw dividers; nav scroll
 * progress; button shimmer. Every motion is useReducedMotion-gated and pointer
 * tilt is additionally matchMedia('(hover:hover)')-gated. One useScroll per
 * section (hero/gear/band); pointer parallax uses motion values (no re-render).
 *
 * Cross-subdomain links (demo.lamirage.live) are plain <a href> (never wouter
 * <Link>, which would 404 into the SPA router). Same-origin (/live) uses <Link>.
 */

import React, { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  useInView,
  animate,
} from "framer-motion";
import {
  Sparkles,
  Rewind,
  Film,
  Wifi,
  Heart,
  Cast,
  Radio,
  ArrowRight,
  Phone,
  Play,
} from "lucide-react";

import { LIVE, goldGrad, goldGradText, pageDarkGrad } from "@/lib/liveTheme";
import {
  GoldShimmerText,
  GoldButton,
  GlassCard,
  LiveBadge,
  SectionLabel,
  GoldRule,
  ExpectMarquee,
} from "@/components/live/LiveUI";
import GearShowcase from "@/components/live/GearShowcase";

// ─── Constants ───────────────────────────────────────────────────────────────

/** Cross-subdomain live demo — MUST be a plain <a href>, not wouter <Link>. */
const DEMO_URL = "https://demo.lamirage.live";

/** Generic place names for the "watched from" marquee (NOT client claims). */
const CITIES = [
  "Kochi", "Dubai", "London", "Toronto", "Sydney", "New Jersey",
  "Doha", "Singapore", "Muscat", "Melbourne", "Abu Dhabi", "Chicago",
];

// ─── Motion helpers ──────────────────────────────────────────────────────────

/**
 * Returns variant props for a slide-up entrance, respects reduced-motion.
 * Pure function (no hooks) — safe to call inside render + hoist above .map().
 */
function getFadeUpVariants(reduced: boolean | null) {
  if (reduced) {
    return {
      hidden: { opacity: 1, y: 0 },
      visible: { opacity: 1, y: 0, transition: { duration: 0 } },
    };
  }
  return {
    hidden: { opacity: 0, y: 28 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
  };
}

/**
 * Depth reveal (y + z + rotateX) — the shared "wow" motion vocabulary used by
 * the gear + trust sections so the whole page reads as one family. Reduced →
 * flat opacity only.
 */
function getDepthVariants(reduced: boolean | null) {
  if (reduced) {
    return {
      hidden: { opacity: 1 },
      visible: { opacity: 1, transition: { duration: 0 } },
    };
  }
  return {
    hidden: { opacity: 0, y: 34, z: -60, rotateX: 6 },
    visible: {
      opacity: 1,
      y: 0,
      z: 0,
      rotateX: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
  };
}

/** Stagger container that sequences children. */
function staggerContainer(stagger = 0.08, reduced: boolean | null) {
  if (reduced) return { hidden: {}, visible: {} };
  return {
    hidden: {},
    visible: { transition: { staggerChildren: stagger } },
  };
}

// ─── fine-pointer detection (SSR-guarded) ────────────────────────────────────
// Hero mouse parallax + player tilt only run on hover-capable, fine-pointer
// devices. matchMedia is only touched inside an effect (never SSR).
function useFinePointer(): boolean {
  const [fine, setFine] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    setFine(mq.matches);
    const onChange = (e: MediaQueryListEvent): void => setFine(e.matches);
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    return undefined;
  }, []);
  return fine;
}

// ─── Section wrapper with whileInView ────────────────────────────────────────

interface FadeInSectionProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  delay?: number;
  reduced: boolean | null;
}

function FadeSection({ children, style, className, delay = 0, reduced }: FadeInSectionProps) {
  const variants = getFadeUpVariants(reduced);
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      variants={{
        hidden: variants.hidden,
        visible: { ...variants.visible, transition: { ...variants.visible.transition, delay } },
      }}
      style={style}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Gold hairline-draw divider (scaleX 0→1 whileInView) ─────────────────────

interface HairlineDrawProps {
  reduced: boolean | null;
  style?: React.CSSProperties;
}

/** Cinematic gold divider that "draws" itself from the centre outward. */
function HairlineDraw({ reduced, style }: HairlineDrawProps) {
  return (
    <motion.div
      aria-hidden="true"
      initial={reduced ? { scaleX: 1 } : { scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={reduced ? { duration: 0 } : { duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      style={{
        height: 1,
        transformOrigin: "center",
        background: `linear-gradient(90deg, transparent 0%, ${LIVE.goldBorder} 50%, transparent 100%)`,
        ...style,
      }}
    />
  );
}

// ─── Mask-wipe shimmer heading span ──────────────────────────────────────────

interface MaskWipeProps {
  children: React.ReactNode;
  reduced: boolean | null;
}

/** Wraps a heading fragment in a left→right clip-path reveal (reduced → shown). */
function MaskWipe({ children, reduced }: MaskWipeProps) {
  return (
    <motion.span
      style={{ display: "inline-block", ...goldGradText }}
      className="font-playfair"
      initial={reduced ? { clipPath: "inset(0 0% 0 0)" } : { clipPath: "inset(0 100% 0 0)" }}
      whileInView={{ clipPath: "inset(0 0% 0 0)" }}
      viewport={{ once: true, margin: "-40px" }}
      transition={reduced ? { duration: 0 } : { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
    >
      {children}
    </motion.span>
  );
}

// ─── CountUp — animated stat numeral (reduced → instant) ─────────────────────

interface CountUpProps {
  to: number;
  suffix?: string;
  reduced: boolean | null;
  style?: React.CSSProperties;
  className?: string;
  duration?: number;
}

/**
 * Counts 0→`to` once, when scrolled into view (framer `animate` + rAF under the
 * hood). Reduced-motion (or a passed `reduced`) renders the final value at once.
 */
function CountUp({ to, suffix = "", reduced, style, className, duration = 1.4 }: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(reduced ? to : 0);

  useEffect(() => {
    if (reduced) {
      setVal(to);
      return;
    }
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, reduced, duration]);

  return (
    <span ref={ref} className={className} style={style}>
      {val}
      {suffix}
    </span>
  );
}

// ─── feature card (GlassCard-based) ─────────────────────────────────────────

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  body: string;
  featured?: boolean;
}

function FeatureCard({ icon, title, body, featured }: FeatureCardProps) {
  return (
    <GlassCard
      style={{
        padding: "1.75rem 1.5rem",
        height: "100%",
        transition: "border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease",
        cursor: "default",
        background: featured ? LIVE.goldDim : undefined,
        borderColor: featured ? LIVE.goldBorderStrong : undefined,
      }}
      className="group ll-rimlight hover:!border-[#C9A962] hover:!shadow-[0_4px_32px_rgba(201,169,98,0.14)] hover:-translate-y-1"
    >
      {/* Icon badge */}
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: LIVE.goldDim,
          border: `1px solid ${LIVE.goldBorder}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "1rem",
          color: LIVE.goldBright,
        }}
      >
        {icon}
      </div>

      <h3
        className="font-playfair"
        style={{ fontSize: "1.05rem", fontWeight: 600, color: "#fff", marginBottom: "0.5rem" }}
      >
        {title}
      </h3>

      <p style={{ fontSize: "0.875rem", color: LIVE.textBody, lineHeight: 1.65, margin: 0 }}>
        {body}
      </p>
    </GlassCard>
  );
}

// ─── step card ───────────────────────────────────────────────────────────────

interface StepProps {
  num: string;
  title: string;
  body: string;
  last?: boolean;
  /** Filled gold badge w/ black numeral + heavier title — for the "Go live" step. */
  emphasized?: boolean;
}

function Step({ num, title, body, last, emphasized }: StepProps) {
  return (
    <div style={{ display: "flex", gap: "1.25rem" }}>
      {/* Number + connector */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 40 }}>
        <div
          className="font-playfair"
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: emphasized ? "none" : `1.5px solid ${LIVE.gold}`,
            color: emphasized ? "#0a0a0a" : LIVE.goldBright,
            background: emphasized ? goldGrad : LIVE.goldDim,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: "1.1rem",
            flexShrink: 0,
            boxShadow: emphasized ? "0 4px 20px rgba(201,169,98,0.35)" : undefined,
          }}
        >
          {num}
        </div>
        {!last && (
          <div
            style={{
              width: 1,
              flex: 1,
              minHeight: 32,
              marginTop: 6,
              background: `linear-gradient(180deg, ${LIVE.goldBorder} 0%, transparent 100%)`,
            }}
          />
        )}
      </div>

      {/* Text */}
      <div style={{ paddingBottom: "2rem" }}>
        <h4
          className="font-playfair"
          style={{
            fontSize: "1.05rem",
            fontWeight: emphasized ? 700 : 600,
            color: "#fff",
            marginBottom: "0.375rem",
          }}
        >
          {title}
        </h4>
        <p style={{ fontSize: "0.875rem", color: LIVE.textBody, lineHeight: 1.65, margin: 0 }}>
          {body}
        </p>
      </div>
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function LiveLanding() {
  const reduced = useReducedMotion();
  const finePointer = useFinePointer();
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [heroVideoOk, setHeroVideoOk] = useState(true);

  // Section refs — one useScroll per section (hero/gear/band), never per-card.
  const heroRef = useRef<HTMLElement>(null);
  const gearRef = useRef<HTMLElement>(null);
  const bandRef = useRef<HTMLElement>(null);

  // Fetch live-now count for hero pill. Resilient — hides on error or zero.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/live/public/live-now", { signal: AbortSignal.timeout(4000) })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: unknown) => {
        if (cancelled) return;
        // data may be an array of entries or {count: number}
        if (Array.isArray(data) && data.length > 0) {
          setLiveCount(data.length);
        } else if (data && typeof (data as { count?: number }).count === "number") {
          const c = (data as { count: number }).count;
          if (c > 0) setLiveCount(c);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const hasLive = liveCount !== null && liveCount > 0;
  const parallaxOn = !reduced;
  const tiltOn = !reduced && finePointer;

  // ── Nav scroll-progress hairline (whole page) ──
  const { scrollYProgress } = useScroll();
  const navProgressScale = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  // ── Hero mouse parallax (motion values → no re-render on move) ──
  const hxRaw = useMotionValue(0);
  const hyRaw = useMotionValue(0);
  const hx = useSpring(hxRaw, { stiffness: 60, damping: 20 });
  const hy = useSpring(hyRaw, { stiffness: 60, damping: 20 });
  // Backdrop bokeh: moves AGAINST the cursor (far plane).
  const bokehX = useTransform(hx, (v) => v * -18);
  const bokehY = useTransform(hy, (v) => v * -18);
  // Copy stack: moves WITH the cursor (near plane).
  const copyX = useTransform(hx, (v) => v * 6);
  // Player frame: subtle 3D tilt toward the cursor.
  const frameRotY = useTransform(hx, (v) => v * 5);
  const frameRotX = useTransform(hy, (v) => v * -5);

  // ── Hero scroll parallax ──
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroFrameY = useTransform(heroScroll, [0, 1], parallaxOn ? [0, -40] : [0, 0]);
  const heroFrameOpacity = useTransform(heroScroll, [0, 1], parallaxOn ? [1, 0.85] : [1, 1]);
  const heroBokehScrollY = useTransform(heroScroll, [0, 1], parallaxOn ? [0, 60] : [0, 0]);

  // ── Cinematic band scroll parallax ──
  const { scrollYProgress: bandScroll } = useScroll({
    target: bandRef,
    offset: ["start end", "end start"],
  });
  const bandTextY = useTransform(bandScroll, [0, 1], parallaxOn ? [30, -30] : [0, 0]);

  function onHeroMove(e: React.PointerEvent<HTMLElement>): void {
    if (!parallaxOn || !finePointer) return;
    const el = heroRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    hxRaw.set((e.clientX - r.left) / r.width - 0.5);
    hyRaw.set((e.clientY - r.top) / r.height - 0.5);
  }
  function onHeroLeave(): void {
    hxRaw.set(0);
    hyRaw.set(0);
  }

  // Motion variants — hoisted once (pure functions, no hooks) for reuse in .map().
  const fadeUp = getFadeUpVariants(reduced);
  const depthUp = getDepthVariants(reduced);
  const heroStagger = staggerContainer(0.09, reduced);
  const featureStagger = staggerContainer(0.07, reduced);
  const stepStagger = staggerContainer(0.12, reduced);
  const trustStagger = staggerContainer(0.09, reduced);

  return (
    <div
      style={{
        position: "relative",
        background: pageDarkGrad,
        color: "#fff",
        minHeight: "100vh",
        overflowX: "hidden",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      {/* ═══ PAGE-WIDE CINEMATIC BACKDROP ═════════════════════════════════ */}
      <div aria-hidden="true" className="ll-cinema">
        <div className="ll-cinema-aurora" />
        <div className="ll-cinema-vignette" />
        <div className="ll-grain" />
      </div>

      {/* ═══ TOP BAR ═══════════════════════════════════════════════════════ */}
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1rem 2rem",
          borderBottom: `1px solid ${LIVE.goldBorder}`,
          background: "rgba(10,10,9,0.92)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        {/* Wordmark */}
        <span
          className="font-playfair"
          style={{
            ...goldGradText,
            fontWeight: 700,
            fontSize: "0.9rem",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            userSelect: "none",
          }}
        >
          LA Mirage
        </span>

        {/* Live-now nav chip */}
        <Link href="/live">
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              border: `1px solid ${LIVE.goldBorder}`,
              borderRadius: 999,
              padding: "5px 14px",
              cursor: "pointer",
              color: LIVE.goldBright,
              transition: "border-color 0.15s, background 0.15s",
            }}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = LIVE.goldDim;
              el.style.borderColor = LIVE.goldBorderStrong;
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = "transparent";
              el.style.borderColor = LIVE.goldBorder;
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: "inline-block",
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: LIVE.live,
                boxShadow: `0 0 7px rgba(232,47,47,0.80)`,
              }}
            />
            Live now
          </span>
        </Link>

        {/* Nav scroll-progress hairline (reduced → hidden via CSS) */}
        <motion.div
          aria-hidden="true"
          className="ll-nav-progress"
          style={{ scaleX: navProgressScale, transformOrigin: "left" }}
        />
      </nav>

      {/* ═══ HERO — cinematic 4K player frame ═══════════════════════════════ */}
      <section
        ref={heroRef}
        onPointerMove={onHeroMove}
        onPointerLeave={onHeroLeave}
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          minHeight: "92vh",
          padding: "5rem 1.5rem",
          overflow: "hidden",
        }}
      >
        {/* Backdrop bokeh plane (far — moves against cursor + down on scroll) */}
        <motion.div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: "-10%",
            zIndex: 0,
            pointerEvents: "none",
            x: parallaxOn ? bokehX : 0,
            y: parallaxOn ? heroBokehScrollY : 0,
            background:
              "radial-gradient(48% 42% at 72% 30%, color-mix(in srgb, var(--lw-accent, #c9a962) 12%, transparent) 0%, transparent 60%), radial-gradient(40% 40% at 20% 70%, color-mix(in srgb, var(--lw-accent, #c9a962) 8%, transparent) 0%, transparent 60%)",
          }}
        />

        {/* Gold dust motes (reduced-motion-safe, reused from watch page) */}
        <div className="lw-dust" style={{ position: "absolute", inset: 0, zIndex: 1 }} aria-hidden="true">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className="lw-dust-mote" />
          ))}
        </div>

        <div className="ll-hero-grid" style={{ position: "relative", zIndex: 2 }}>
          {/* ── LEFT: copy stack (near plane — drifts with cursor) ── */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroStagger}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.4rem",
              position: "relative",
              zIndex: 1,
              x: parallaxOn ? copyX : 0,
            }}
          >
            {/* Live-now pill (or static eyebrow so the slot never collapses) */}
            {hasLive ? (
              <motion.div variants={fadeUp}>
                <Link href="/live">
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                      border: `1px solid ${LIVE.goldBorder}`,
                      background: LIVE.goldDim,
                      color: LIVE.goldBright,
                      borderRadius: 999,
                      padding: "6px 16px",
                      cursor: "pointer",
                      textTransform: "uppercase",
                    }}
                  >
                    <LiveBadge status="live" />
                    {liveCount} {liveCount === 1 ? "wedding" : "weddings"} streaming live
                  </span>
                </Link>
              </motion.div>
            ) : (
              <motion.div variants={fadeUp}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: LIVE.goldBright,
                  }}
                >
                  <Radio size={13} strokeWidth={2.25} aria-hidden="true" />
                  Cinematic wedding broadcasts
                </span>
              </motion.div>
            )}

            {/* Eyebrow */}
            <motion.div variants={fadeUp} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <GoldRule />
              <SectionLabel>LA Mirage · Live Broadcasts</SectionLabel>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={fadeUp}
              className="font-playfair"
              style={{
                fontSize: "clamp(2.6rem, 6vw, 4.6rem)",
                fontWeight: 700,
                lineHeight: 1.06,
                letterSpacing: "-0.02em",
                margin: 0,
                textAlign: "left",
              }}
            >
              <span style={{ color: "#fff" }}>Every face that matters, in the room</span>{" "}
              <GoldShimmerText>or 4,000 miles away.</GoldShimmerText>
            </motion.h1>

            {/* Subhead */}
            <motion.p
              variants={fadeUp}
              style={{
                fontSize: "clamp(1rem, 2vw, 1.18rem)",
                color: LIVE.textBody,
                maxWidth: 460,
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              We broadcast your wedding in true 4K — cinematic, multi-camera,
              flawless on any screen — so no one who loves you ever misses a
              moment. You do nothing on the day.
            </motion.p>

            {/* CTAs */}
            <motion.div
              variants={fadeUp}
              style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14, marginTop: "0.25rem" }}
            >
              {/* Cross-subdomain → plain <a href>, NOT wouter Link */}
              <GoldButton variant="solid" href={DEMO_URL} className="ll-btn-shimmer" aria-label="Watch a live demo broadcast">
                Watch a live demo
                <ArrowRight size={15} strokeWidth={2.5} aria-hidden="true" />
              </GoldButton>

              {/* Same-origin → wouter Link */}
              <Link href="/live">
                <GoldButton variant="ghost" className="ll-btn-shimmer">
                  See what's live now
                  {hasLive && (
                    <span
                      aria-hidden="true"
                      style={{
                        display: "inline-block",
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: LIVE.live,
                        marginLeft: 2,
                        boxShadow: `0 0 7px rgba(232,47,47,0.80)`,
                      }}
                    />
                  )}
                </GoldButton>
              </Link>
            </motion.div>

            {/* Trust microline */}
            <motion.p
              variants={fadeUp}
              style={{ fontSize: "0.78rem", color: LIVE.textMuted, margin: 0, letterSpacing: "0.02em" }}
            >
              Included with weddings at LA Mirage · Kochi, Kerala
            </motion.p>
          </motion.div>

          {/* ── RIGHT: cinematic player frame (3D tilt + scroll parallax) ── */}
          <motion.div
            style={{
              perspective: 1200,
              y: parallaxOn ? heroFrameY : 0,
              opacity: parallaxOn ? heroFrameOpacity : 1,
            }}
          >
            <motion.div
              className="lw-player-glow lw-player-frame lw-player-fade lw-rise lw-rise-2 ll-hero-frame"
              style={{
                rotateX: tiltOn ? frameRotX : 0,
                rotateY: tiltOn ? frameRotY : 0,
                transformStyle: "preserve-3d",
              }}
            >
            {/* Base layer: hero video (content dependency — degrades gracefully).
                Gated OFF under reduced-motion (poster only). */}
            {!reduced && heroVideoOk && (
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/live/hero-poster.jpg"
                onError={() => setHeroVideoOk(false)}
              >
                <source src="/live/hero-loop.webm" type="video/webm" />
                <source src="/live/hero-loop.mp4" type="video/mp4" />
              </video>
            )}
            {/* Reduced-motion: poster still frame only (no autoplay video) */}
            {reduced && heroVideoOk && (
              <img
                src="/live/hero-poster.jpg"
                alt=""
                aria-hidden="true"
                onError={() => setHeroVideoOk(false)}
                style={{ position: "absolute", inset: 0, zIndex: 1, width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}

            {/* ── Always-on overlay chrome (carries meaning even w/o video) ── */}

            {/* Top-left: LIVE badge */}
            <div className="lw-overlay-badge" style={{ top: 14, left: 14, zIndex: 20 }}>
              <LiveBadge status="live" />
            </div>

            {/* Top-right: static "4K" glass pill (DVR-pill styling) */}
            <div className="lw-overlay-badge" style={{ top: 14, right: 14, zIndex: 20 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "rgba(10,10,9,0.78)",
                  border: `1px solid ${LIVE.goldBorderStrong}`,
                  borderRadius: 999,
                  color: LIVE.gold,
                  fontFamily: "Inter, system-ui, sans-serif",
                  fontSize: "0.70rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  padding: "6px 14px",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                }}
              >
                4K
              </span>
            </div>

            {/* Bottom-left: caption chip */}
            <div
              style={{
                position: "absolute",
                bottom: 14,
                left: 14,
                zIndex: 20,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(10,10,9,0.72)",
                border: `1px solid ${LIVE.goldBorder}`,
                borderRadius: 12,
                padding: "8px 14px",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
            >
              <span className="font-playfair" style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>
                The wedding of
              </span>
              <span style={{ color: LIVE.goldBright, fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                · Live now
              </span>
            </div>

            {/* Bottom-right: "watching" pill */}
            <div
              style={{
                position: "absolute",
                bottom: 14,
                right: 14,
                zIndex: 20,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(10,10,9,0.72)",
                border: `1px solid ${LIVE.goldBorder}`,
                borderRadius: 999,
                color: LIVE.textBody,
                fontFamily: "Inter, system-ui, sans-serif",
                fontSize: "0.70rem",
                fontWeight: 600,
                letterSpacing: "0.04em",
                padding: "6px 14px",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
            >
              <Heart size={12} strokeWidth={2} aria-hidden="true" style={{ color: LIVE.goldBright }} />
              128 watching
            </div>

            {/* Drifting hearts (reduced-motion hides via CSS) */}
            <Heart className="ll-heart ll-heart--1" size={16} strokeWidth={1.75} aria-hidden="true" />
            <Heart className="ll-heart ll-heart--2" size={20} strokeWidth={1.75} aria-hidden="true" />
            <Heart className="ll-heart ll-heart--3" size={14} strokeWidth={1.75} aria-hidden="true" />
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll cue (below CTAs / hero) — reduced → static via CSS */}
        <div className="ll-scroll-cue" aria-hidden="true">
          <span className="ll-scroll-cue-line">
            <span className="ll-scroll-cue-dot" />
          </span>
          <span className="ll-scroll-cue-label">Scroll</span>
        </div>

        {/* Bottom section divider hairline (draws in) */}
        <HairlineDraw reduced={reduced} style={{ position: "absolute", bottom: 0, left: 0, right: 0 }} />
      </section>

      {/* ═══ THE KIT BEHIND YOUR BROADCAST (3D gear showcase) ══════════════ */}
      <section ref={gearRef} className="ll-gear-section">
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <FadeSection
            style={{ textAlign: "center", marginBottom: "3rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}
            reduced={reduced}
          >
            <SectionLabel>The kit behind your broadcast</SectionLabel>
            <GoldRule />
            <h2
              className="font-playfair"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 3rem)",
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              Broadcast-grade gear.{" "}
              <MaskWipe reduced={reduced}>Run by people, not luck.</MaskWipe>
            </h2>
            <p
              style={{
                fontSize: "clamp(1rem, 2vw, 1.18rem)",
                color: LIVE.textBody,
                lineHeight: 1.7,
                maxWidth: 620,
                margin: 0,
              }}
            >
              The same cinema bodies, robotic PTZ heads and live-production
              switcher a television crew would bring — set up, tested and
              operated entirely by our team.
            </p>
          </FadeSection>

          <GearShowcase reduced={reduced} />
        </div>
      </section>

      {/* ═══ WHAT WE DELIVER ════════════════════════════════════════════════ */}
      <section
        style={{
          position: "relative",
          zIndex: 1,
          padding: "6rem 1.5rem",
          background: LIVE.bgCard,
          borderTop: `1px solid ${LIVE.goldBorder}`,
          borderBottom: `1px solid ${LIVE.goldBorder}`,
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {/* Section heading */}
          <FadeSection
            style={{ textAlign: "center", marginBottom: "2.5rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}
            reduced={reduced}
          >
            <SectionLabel>What we deliver</SectionLabel>
            <GoldRule />
            <h2
              className="font-playfair"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 3rem)",
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              A broadcast worthy of the day.{" "}
              <MaskWipe reduced={reduced}>Not a phone on a tripod.</MaskWipe>
            </h2>
            <p
              style={{
                fontSize: "clamp(1rem, 2vw, 1.18rem)",
                color: LIVE.textBody,
                lineHeight: 1.7,
                maxWidth: 560,
                margin: 0,
              }}
            >
              Everything below is included and run entirely by our team. You just
              say "go live."
            </p>
          </FadeSection>

          {/* Continuity element — the same marquee the watch page shows */}
          <FadeSection style={{ marginBottom: "3rem" }} reduced={reduced}>
            <ExpectMarquee />
          </FadeSection>

          {/* Feature grid with staggered entrance */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            variants={featureStagger}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))",
              gap: "1.25rem",
            }}
          >
            {[
              {
                icon: <Sparkles size={20} />,
                title: "True 4K Ultra HD",
                body: "Cinematic, multi-camera coverage in true 4K — every glance, tear and detail, crisp enough to feel like you're in the front row.",
                featured: true,
              },
              {
                icon: <Rewind size={20} />,
                title: "Live Rewind",
                body: "Guests who join late simply rewind to the very first moment. No one misses the entrance, the vows, or the first kiss.",
              },
              {
                icon: <Film size={20} />,
                title: "Kept as a keepsake",
                body: "The film stays on your private page for months after the day — rewatch the ceremony whenever you want to feel it again.",
              },
              {
                icon: <Wifi size={20} />,
                title: "Flawless on any connection",
                body: "The stream adapts to every device and network, so family on hotel Wi-Fi see it as beautifully as those on fibre.",
              },
              {
                icon: <Heart size={20} />,
                title: "Cheer from afar",
                body: "Loved ones send live wishes and floating hearts in real time — the room feels full, even for guests who couldn't fly in.",
              },
              {
                icon: <Cast size={20} />,
                title: "Watch anywhere",
                body: "Phone, laptop, or the big screen — AirPlay and Chromecast bring your wedding to the living-room TV in one tap. No apps.",
              },
              {
                icon: <Radio size={20} />,
                title: "We run everything",
                body: "Our team sets up, tests, and broadcasts the entire day. You do nothing but get married.",
                featured: true,
              },
            ].map((feat) => (
              <motion.div key={feat.title} variants={fadeUp}>
                <FeatureCard icon={feat.icon} title={feat.title} body={feat.body} featured={feat.featured} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ CINEMATIC FULL-BLEED BAND ══════════════════════════════════════ */}
      <section ref={bandRef} className="ll-band">
        <div className="lw-grain" aria-hidden="true" />
        <div className="ll-band-glow" aria-hidden="true" />
        <motion.div
          className="ll-band-inner"
          style={{ y: parallaxOn ? bandTextY : 0 }}
        >
          <SectionLabel>Filmed, not recorded</SectionLabel>
          <h2
            className="font-playfair"
            style={{
              fontSize: "clamp(2.4rem, 6vw, 4rem)",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1.12,
              margin: "1rem 0 0",
              letterSpacing: "-0.02em",
            }}
          >
            Six cameras. One operator.{" "}
            <MaskWipe reduced={reduced}>Zero moments missed.</MaskWipe>
          </h2>
        </motion.div>
      </section>

      {/* ═══ YOU DO NOTHING ON THE DAY (white-glove how-it-works) ═══════════ */}
      <section
        id="how-it-works"
        style={{
          position: "relative",
          zIndex: 1,
          padding: "6rem 1.5rem",
          borderBottom: `1px solid ${LIVE.goldBorder}`,
        }}
      >
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <FadeSection
            style={{ textAlign: "center", marginBottom: "3.5rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}
            reduced={reduced}
          >
            <SectionLabel>How it works</SectionLabel>
            <GoldRule />
            <h2
              className="font-playfair"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 3rem)",
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              Effortless for you.{" "}
              <MaskWipe reduced={reduced}>Unforgettable for them.</MaskWipe>
            </h2>
          </FadeSection>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-30px" }}
            variants={stepStagger}
          >
            {[
              {
                num: "1",
                title: "Add Live to your wedding",
                body: "Tell us you'd like your day broadcast. We handle the rest — cameras, crew, streaming, all of it.",
              },
              {
                num: "2",
                title: "We set up and test — quietly",
                body: "Our team arrives early, positions cameras, and runs a full test long before your first guest does.",
              },
              {
                num: "3",
                title: "Go live in 4K",
                body: "At the moment you choose, your celebration streams in cinematic 4K to everyone with your private link.",
                emphasized: true,
              },
              {
                num: "4",
                title: "Keep the film forever-ish",
                body: "After the last dance, the full broadcast becomes a private keepsake on your page, ready to rewatch for months.",
                last: true,
              },
            ].map((step) => (
              <motion.div key={step.num} variants={fadeUp}>
                <Step {...step} />
              </motion.div>
            ))}
          </motion.div>

          {/* Closing reassurance line */}
          <FadeSection reduced={reduced}>
            <p
              style={{
                fontStyle: "italic",
                color: LIVE.textMuted,
                textAlign: "center",
                fontSize: "0.95rem",
                lineHeight: 1.7,
                margin: "1.5rem 0 0",
              }}
            >
              No apps for your guests. No stress for you. Just one link to share.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* ═══ PROOF / TRUST BAND ═════════════════════════════════════════════ */}
      <section
        style={{
          position: "relative",
          zIndex: 1,
          padding: "5rem 1.5rem",
          background: LIVE.bgCard,
          borderTop: `1px solid ${LIVE.goldBorder}`,
          borderBottom: `1px solid ${LIVE.goldBorder}`,
        }}
      >
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <FadeSection
            style={{ textAlign: "center", marginBottom: "2.75rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}
            reduced={reduced}
          >
            <GoldRule />
            <SectionLabel>Trusted for the moments that matter</SectionLabel>
          </FadeSection>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            variants={trustStagger}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
              gap: "1.25rem",
              perspective: 1200,
            }}
          >
            {/* Tile 1 — 4K (animated 0→4) */}
            <motion.div variants={depthUp} style={{ transformStyle: "preserve-3d" }}>
              <GlassCard style={{ padding: "2rem 1.5rem", height: "100%", textAlign: "center" }}>
                <div className="font-playfair" style={{ ...goldGradText, fontSize: "2.6rem", fontWeight: 700, lineHeight: 1, marginBottom: "0.6rem" }}>
                  <CountUp to={4} suffix="K" reduced={reduced} />
                </div>
                <p style={{ fontSize: "0.875rem", color: LIVE.textBody, lineHeight: 1.6, margin: 0 }}>
                  True Ultra-HD, multi-camera
                </p>
              </GlassCard>
            </motion.div>

            {/* Tile 2 — Cameras (animated 0→6) */}
            <motion.div variants={depthUp} style={{ transformStyle: "preserve-3d" }}>
              <GlassCard style={{ padding: "2rem 1.5rem", height: "100%", textAlign: "center" }}>
                <div className="font-playfair" style={{ ...goldGradText, fontSize: "2.6rem", fontWeight: 700, lineHeight: 1, marginBottom: "0.6rem" }}>
                  <CountUp to={6} reduced={reduced} />
                </div>
                <p style={{ fontSize: "0.875rem", color: LIVE.textBody, lineHeight: 1.6, margin: 0 }}>
                  Cameras &amp; PTZ heads on the day
                </p>
              </GlassCard>
            </motion.div>

            {/* Tile 3 — keepsake (word) */}
            <motion.div variants={depthUp} style={{ transformStyle: "preserve-3d" }}>
              <GlassCard style={{ padding: "2rem 1.5rem", height: "100%", textAlign: "center" }}>
                <div className="font-playfair" style={{ ...goldGradText, fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.1, marginBottom: "0.6rem" }}>
                  For months
                </div>
                <p style={{ fontSize: "0.875rem", color: LIVE.textBody, lineHeight: 1.6, margin: 0 }}>
                  Your film, kept as a private keepsake
                </p>
              </GlassCard>
            </motion.div>
          </motion.div>

          {/* Soft social-proof line (no invented ratings/quotes/counts/names) */}
          <FadeSection reduced={reduced}>
            <p
              style={{
                fontStyle: "italic",
                color: LIVE.textMuted,
                textAlign: "center",
                fontSize: "0.95rem",
                lineHeight: 1.7,
                maxWidth: 620,
                margin: "2.25rem auto 0",
              }}
            >
              Chosen by couples celebrating at LA Mirage to bring family across the
              world into the room.
            </p>
          </FadeSection>

          {/* Couple-city marquee (generic place names, not client claims) */}
          <FadeSection reduced={reduced} style={{ marginTop: "2rem", textAlign: "center" }}>
            <p
              style={{
                fontSize: "0.62rem",
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: LIVE.textMuted,
                margin: "0 0 0.9rem",
              }}
            >
              Watched from
            </p>
            <div className="lw-expect ll-city-marquee" aria-label="Watched from cities around the world">
              <div className="lw-expect-track">
                {[...CITIES, ...CITIES].map((city, i) => (
                  <span key={i} className="ll-city" aria-hidden={i >= CITIES.length || undefined}>
                    {city}
                    <span className="ll-city-dot" aria-hidden="true">·</span>
                  </span>
                ))}
              </div>
            </div>
          </FadeSection>

          {/* Testimonial slot — renders ONLY when real content exists.
              Drop a { quote, attribution } object into TESTIMONIAL to ship one. */}
          {(() => {
            const TESTIMONIAL: { quote: string; attribution: string } | null = null;
            if (!TESTIMONIAL) return null;
            return (
              <FadeSection reduced={reduced} style={{ marginTop: "2.5rem" }}>
                <GlassCard style={{ padding: "2.25rem 2rem", maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
                  <p className="font-playfair" style={{ color: "#fff", fontSize: "1.15rem", fontStyle: "italic", lineHeight: 1.6, margin: 0 }}>
                    "{TESTIMONIAL.quote}"
                  </p>
                  <p style={{ color: LIVE.goldBright, fontSize: "0.78rem", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", margin: "1rem 0 0" }}>
                    {TESTIMONIAL.attribution}
                  </p>
                </GlassCard>
              </FadeSection>
            );
          })()}
        </div>
      </section>

      {/* ═══ SAMPLE / DEMO STRIP ════════════════════════════════════════════ */}
      <section style={{ position: "relative", zIndex: 1, padding: "6rem 1.5rem" }}>
        <FadeSection style={{ maxWidth: 1000, margin: "0 auto" }} reduced={reduced}>
          <GlassCard style={{ padding: 0, overflow: "hidden" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                alignItems: "stretch",
              }}
              className="ll-demo-grid"
            >
              {/* LEFT: mini 16:9 player frame (poster + gold play triangle) */}
              <div style={{ padding: "1.75rem", perspective: 1200 }}>
                <div className="lw-player-glow lw-player-frame ll-hero-frame ll-demo-frame" style={{ margin: 0 }}>
                  {/* Poster still (falls back to the themed aurora bg on 404) */}
                  <img
                    src="/live/hero-poster.jpg"
                    alt=""
                    aria-hidden="true"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                    style={{ position: "absolute", inset: 0, zIndex: 1, width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {/* LIVE badge */}
                  <div className="lw-overlay-badge" style={{ top: 12, left: 12, zIndex: 20 }}>
                    <LiveBadge status="live" />
                  </div>
                  {/* Gold play triangle */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        background: "rgba(10,10,9,0.55)",
                        border: `1px solid ${LIVE.goldBorderStrong}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backdropFilter: "blur(6px)",
                        WebkitBackdropFilter: "blur(6px)",
                        boxShadow: `0 0 32px rgba(201,169,98,0.3)`,
                      }}
                    >
                      <Play size={26} fill={LIVE.goldBright} strokeWidth={0} style={{ marginLeft: 3 }} aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </div>

              {/* RIGHT: copy + CTAs */}
              <div style={{ padding: "1.75rem 2rem", display: "flex", flexDirection: "column", justifyContent: "center", gap: "1rem" }}>
                <SectionLabel>See it live</SectionLabel>
                <h3
                  className="font-playfair"
                  style={{ fontSize: "clamp(1.5rem, 3vw, 1.8rem)", fontWeight: 700, color: "#fff", lineHeight: 1.2, margin: 0 }}
                >
                  Watch a real broadcast, right now.
                </h3>
                <p style={{ fontSize: "0.875rem", color: LIVE.textBody, lineHeight: 1.65, margin: 0 }}>
                  Our live demo runs the exact experience your guests will see — the
                  cinematic player, the countdown, the wishes. Take a look before your day.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14, marginTop: "0.25rem" }}>
                  {/* Cross-subdomain → plain <a href> */}
                  <GoldButton variant="solid" href={DEMO_URL} className="ll-btn-shimmer" aria-label="Open the live demo broadcast">
                    Open the live demo
                    <ArrowRight size={15} strokeWidth={2.5} aria-hidden="true" />
                  </GoldButton>
                  {/* Same-origin → wouter Link */}
                  <Link href="/live">
                    <GoldButton variant="ghost" className="ll-btn-shimmer">Browse what's streaming</GoldButton>
                  </Link>
                </div>
              </div>
            </div>
          </GlassCard>
        </FadeSection>
      </section>

      {/* ═══ CLOSING CTA BAND ═══════════════════════════════════════════════ */}
      <section
        style={{
          position: "relative",
          zIndex: 1,
          padding: "5rem 1.5rem",
          background: LIVE.bgCard,
          borderTop: `1px solid ${LIVE.goldBorder}`,
        }}
      >
        <FadeSection style={{ maxWidth: 680, margin: "0 auto" }} reduced={reduced}>
          <GlassCard style={{ padding: "3.5rem 2.5rem", textAlign: "center", position: "relative" }}>
            <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "1.25rem" }}>
              <GoldRule />
              <SectionLabel>Bring everyone in</SectionLabel>

              <h2
                className="font-playfair"
                style={{
                  fontSize: "clamp(1.9rem, 4.5vw, 3rem)",
                  fontWeight: 700,
                  color: "#fff",
                  lineHeight: 1.2,
                  margin: 0,
                }}
              >
                Getting married at{" "}
                <GoldShimmerText>LA Mirage?</GoldShimmerText>
              </h2>

              <p
                style={{
                  fontSize: "1rem",
                  color: LIVE.textBody,
                  lineHeight: 1.72,
                  maxWidth: 480,
                  margin: 0,
                }}
              >
                Ask us to add Live to your package. We'll broadcast your day in 4K
                to everyone who couldn't be there — and hand you the film to keep.
                You won't lift a finger.
              </p>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 16,
                  justifyContent: "center",
                  marginTop: "0.5rem",
                }}
              >
                {/* Cross-subdomain → plain <a href> */}
                <GoldButton variant="solid" href={DEMO_URL} className="ll-btn-shimmer" aria-label="Watch the live demo broadcast">
                  Watch the live demo
                  <ArrowRight size={15} strokeWidth={2.5} aria-hidden="true" />
                </GoldButton>

                {/* Public venue line — do NOT change */}
                <a
                  href="tel:+919446776666"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: LIVE.goldBright,
                    textDecoration: "none",
                    transition: "color 0.15s",
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = LIVE.goldPale; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = LIVE.goldBright; }}
                  aria-label="Call LA Mirage venue at +91 94467 76666"
                >
                  <Phone size={14} aria-hidden="true" />
                  +91 94467 76666
                </a>
              </div>
            </div>
          </GlassCard>
        </FadeSection>
      </section>

      {/* ═══ FOOTER ══════════════════════════════════════════════════════════ */}
      <footer
        style={{
          position: "relative",
          zIndex: 1,
          padding: "2rem 2rem",
          borderTop: `1px solid ${LIVE.goldBorder}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
        }}
      >
        {/* Slim center link row */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 14 }}>
          {/* Cross-subdomain → plain <a href> */}
          <a
            href={DEMO_URL}
            style={{ fontSize: "0.75rem", color: LIVE.textMuted, textDecoration: "none", transition: "color 0.15s" }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = LIVE.gold; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = LIVE.textMuted; }}
          >
            Watch a demo
          </a>
          <span aria-hidden="true" style={{ color: LIVE.goldBorder, fontSize: "0.75rem" }}>·</span>
          {/* Same-origin → wouter Link */}
          <Link href="/live">
            <span
              style={{ fontSize: "0.75rem", color: LIVE.textMuted, textDecoration: "none", cursor: "pointer", transition: "color 0.15s" }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = LIVE.gold; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = LIVE.textMuted; }}
            >
              What's live
            </span>
          </Link>
          <span aria-hidden="true" style={{ color: LIVE.goldBorder, fontSize: "0.75rem" }}>·</span>
          <a
            href="https://lamirage.co"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: "0.75rem", color: LIVE.textMuted, textDecoration: "none", transition: "color 0.15s" }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = LIVE.gold; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = LIVE.textMuted; }}
          >
            LA Mirage
          </a>
        </div>

        <div
          style={{
            width: "100%",
            maxWidth: 900,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span
            className="font-playfair"
            style={{ color: LIVE.gold, fontSize: "0.9rem", fontWeight: 600, letterSpacing: "0.04em" }}
          >
            LA Mirage Live
          </span>

          <p style={{ fontSize: "0.75rem", color: LIVE.textMuted, margin: 0, textAlign: "center" }}>
            &copy; {new Date().getFullYear()} LA Mirage Luxury Venue. All rights reserved.
          </p>

          <a
            href="https://lamirage.co"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: "0.75rem",
              color: `rgba(201,169,98,0.55)`,
              textDecoration: "none",
              transition: "color 0.15s",
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = LIVE.gold; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = "rgba(201,169,98,0.55)"; }}
            aria-label="Visit the main LA Mirage site"
          >
            lamirage.co
          </a>
        </div>
      </footer>
    </div>
  );
}
